import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, ExternalLink, RefreshCw } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

export function SubscriptionStatusCard() {
  const { subscriptionStatus, checkSubscription, openCustomerPortal, getCurrentPlan } = useSubscription();
  
  const currentPlan = getCurrentPlan();
  const isSubscribed = subscriptionStatus.subscribed;
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Unbekannt";
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "long", 
      day: "numeric",
    });
  };

  if (subscriptionStatus.loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription-Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription-Status
        </CardTitle>
        <CardDescription>
          Verwalten Sie Ihr Abonnement und Zahlungen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Status */}
        <div className="flex items-center justify-between">
          <span className="font-medium">Status:</span>
          {isSubscribed ? (
            <Badge variant="default" className="bg-green-100 text-green-800">
              Aktiv
            </Badge>
          ) : (
            <Badge variant="secondary">
              Kostenlos
            </Badge>
          )}
        </div>

        {/* Current Plan */}
        <div className="flex items-center justify-between">
          <span className="font-medium">Aktueller Plan:</span>
          <span className="font-semibold">
            {currentPlan ? currentPlan.name : "Free Plan"}
          </span>
        </div>

        {/* Price */}
        {currentPlan && (
          <div className="flex items-center justify-between">
            <span className="font-medium">Preis:</span>
            <span className="font-semibold">
              {currentPlan.price}€/Monat
            </span>
          </div>
        )}

        {/* Subscription End Date */}
        {isSubscribed && subscriptionStatus.subscription_end && (
          <div className="flex items-center justify-between">
            <span className="font-medium">Nächste Abrechnung:</span>
            <span>
              {formatDate(subscriptionStatus.subscription_end)}
            </span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={checkSubscription}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Status aktualisieren
          </Button>

          {isSubscribed && (
            <Button
              variant="outline"
              size="sm"
              onClick={openCustomerPortal}
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Abonnement verwalten
            </Button>
          )}
        </div>

        {/* Upgrade Notice for Free Users */}
        {!isSubscribed && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm text-blue-800">
              <strong>Kostenloses Konto</strong>
              <p className="mt-1">
                Upgrade auf einen bezahlten Plan für erweiterte Funktionen und unbegrenzte Nutzung.
              </p>
            </div>
          </div>
        )}

        {/* Active Subscription Notice */}
        {isSubscribed && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-sm text-green-800">
              <strong>Aktives Abonnement</strong>
              <p className="mt-1">
                Sie haben vollen Zugang zu allen {currentPlan?.name} Funktionen.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}